import { nameColumn } from "../../../layouts/Table/columns/nameColumn";
import dateColumn from "../../../layouts/Table/columns/dateColumn";
import { useLanguage } from "../../../stores/LanguageStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Avatar,
  Box,
  CloseButton,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import MainTable from "../../../layouts/Table/MainTable";
import {
  IconChevronDown,
  IconFolderShare,
  IconGripVertical,
} from "@tabler/icons-react";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { tagsColumn } from "../../../layouts/Table/columns/tagsColumn";
import { sharingColumn } from "../../../layouts/Table/columns/sharingColumn";
import { Link, useNavigate, useParams } from "react-router-dom";
import SelectionDeleter from "../../../components/TableComponents/SelectionDeleter";
import { useListingLoader } from "../../../components/hooks/ListingHooks";
import CreateModel from "./CreateModel";
import { getModelTypeProps, parseModelType } from "./modelUtils";
import numberColumn from "../../../layouts/Table/columns/numberColumn";
import ModelActions from "./actions/ModelActions";
import SelectionMover from "../../../components/TableComponents/SelectionMover";
import SelectionSharer from "../../../components/TableComponents/SelectionSharer";
import { useListStore } from "../../../stores/ListStore";
import ModelPageEdit from "./ModelPageEdit";
import ModelTemplates from "./ModelTemplates";
import { getRowClasses } from "../lists/ListingWithContent/listing";
import { getTypeIcon } from "../workflows/CreateCampaign/flow/flowSettings";
import FoldersDropdown from "../lists/ListingWithContent/contacts/FoldersDropdown";
import NoItemsModal from "../../../components/TableComponents/NoItemsModal";
import SelectionDuplicator from "../../../components/TableComponents/SelectionDuplicator";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useConfigStore } from "../../../stores/ConfigStore";
import ModelActionColumn from "./actions/ModelActionColumn";
import { getFiles } from "../../../api/FIleAPI";
import ModelTypeFilter from "./ModelTypeFilter";
import { camelToSnake, snakeToCamel } from "../../../functions/utilFunctions";

const ModelListing = () => {
  const { folderId, modelType, modelId } = useParams();
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);
  const model_id = useMemo(() => parseInt(modelId) || null, [modelId]);
  const [hasQuery, setHasQuery] = useState(false);
  const [typeFilter, setTypeFilter] = useState([]);

  const columns = useMemo(() => {
    return [
      numberColumn(),
      nameColumn({
        handleClick: () => {},
        disableLink: true,
        editFunction: (row) =>
          getModelTypeProps(modelType, folderId, row)?.editFunction,
        apiRef,
        width: 350,
      }),
      ...(folderId
        ? [
            {
              field: "model_type",
              headerName: lang.model_folder.columns.model_type,
              width: 100,
              renderCell: ({ row, value }) => {
                return (
                  <Tooltip label={value?.split(/(?=[A-Z])/).join(" ")}>
                    {getTypeIcon(
                      row?.model_type,
                      false,
                      24,
                      {
                        color: row?.model_type.includes("mail")
                          ? "var(--mantine-color-secondary-6)"
                          : "var(--mantine-color-blue-6)",
                        variant: "subtle",
                        radius: "md",
                      },
                      true,
                    )}
                  </Tooltip>
                );
              },
            },
          ]
        : getModelTypeProps(modelType)?.columns || []),
      tagsColumn({
        apiRef,
        editFunction: (row) =>
          getModelTypeProps(modelType, folderId, row)?.editFunction,
      }),
      sharingColumn({
        apiRef,
        editFunction: (row) =>
          getModelTypeProps(modelType, folderId, row)?.editFunction,
        allowResellerCustomers: true,
      }),
      dateColumn(),
    ];
  }, []);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [highlight, setHighlight] = useState(null);
  const [minState, setMinState] = useState("opened");

  const navigate = useNavigate();
  const handleRowClick = ({ row }) => {
    const setMinimized = useListStore.getState().setModelMinimized;

    if (folderId) {
      if (model_id === row.id) {
        navigate(`/models/folder/${folderId}/model/none/type`);
      } else {
        navigate(
          `/models/folder/${folderId}/model/${row.id}/${parseModelType(
            row.model_type,
          )}`,
        );
        setMinimized(true);
      }
    } else {
      if (model_id === row.id) {
        navigate(`/models/${modelType}/none`);
      } else {
        navigate(`/models/${modelType}/${row.id}`);
        setMinimized(true);
      }
    }
  };

  const handleClose = () => {
    if (folderId) {
      navigate(`/models/folder/${folderId}/model/none/type`);
    } else {
      navigate(`/models/${modelType}/none`);
    }
    setMinState("opened");
  };

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <ModelActionColumn
          handleRowClick={handleRowClick}
          hasQuery={hasQuery}
          row={row}
          setLoading={setLoading}
          inFolder={!!folderId}
        />
      );
    },
    [hasQuery],
  );

  const toolbarActions = useCallback(
    ({ selectedIds, handleClose }) => (
      <>
        <SelectionDuplicator
          getHandler={(current) =>
            getModelTypeProps(modelType, folderId, current)?.copyFunction
          }
          setTableLoading={setLoading}
          ids={selectedIds}
          rows={rows}
          closeMenu={handleClose}
          apiRef={apiRef}
        />

        <SelectionMover
          getEditFunction={(row) =>
            getModelTypeProps(modelType, folderId, row)?.editFunction
          }
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
          folderType={"Model"}
          closeMenu={handleClose}
          rows={rows}
        />

        <SelectionSharer
          getEditFunction={(row) =>
            getModelTypeProps(modelType, folderId, row)?.editFunction
          }
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
          closeMenu={handleClose}
          rows={rows}
        />

        <SelectionDeleter
          getHandler={(row) =>
            getModelTypeProps(modelType, folderId, row)?.deleteFunction
          }
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
          closeMenu={handleClose}
          rows={rows}
        />
      </>
    ),
    [apiRef, modelType, rows],
  );

  // useEffect(() => {
  //   if (model_id) {
  //     apiRef.current.setColumnVisibility("created_on", false);
  //   } else {
  //     apiRef.current.setColumnVisibility("created_on", true);
  //   }
  // }, [apiRef, model_id]);

  useEffect(() => {
    if (folderId !== "none") setLoading(true);
  }, [folderId]);

  useListingLoader({
    loading,
    setLoading,
    listGetter: getModelTypeProps(modelType, false)?.listingFunction,
    responseField:
      modelType === "email-signature" ? "email_signatures_list" : "models_list",
    setRows,
    folderId,
    getRows:
      modelType === "linkedin-voice-message"
        ? (result) =>
            result
              .filter((r) =>
                useConfigStore
                  .getState()
                  .files_list.filter(
                    (f) =>
                      f.file_url.endsWith(".mp3") ||
                      f.file_url.endsWith(".wav"),
                  )
                  .some((f) => f.id === r.file_id),
              )
              .sort((a, b) => b.id - a.id)
        : modelType === "linkedin-message"
          ? (result) =>
              result
                .filter((r) => {
                  if (r.file_id) {
                    return useConfigStore
                      .getState()
                      .files_list.filter(
                        (f) =>
                          !f.file_url.endsWith(".mp3") &&
                          !f.file_url.endsWith(".wav"),
                      )
                      .some((f) => f.id === r.file_id);
                  }
                  return true;
                })
                .sort((a, b) => b.id - a.id)
          : undefined,
  });

  const filteredRows = useMemo(() => {
    if (folderId) {
      if (typeFilter.length === 0) return rows;
      else
        return rows.filter((row) =>
          typeFilter.includes(camelToSnake(row.model_type)),
        );
    }
    return rows.filter((x) => !x.folder);
  }, [folderId, rows, typeFilter]);

  return (
    <Stack flex={1} px={48} pt={16} gap={0} h={"calc(100vh - 64px)"}>
      <Group gap={"xl"}>
        <Text size="xl" fw={600} mt={16} mb={8} mr={"auto"}>
          {lang.new_models.default_title}{" "}
          {getModelTypeProps(modelType, !!folderId)?.name}
        </Text>
        {modelType === "email" && (
          <ModelTemplates
            onModelCreated={handleClose}
            setTableLoading={setLoading}
          />
        )}
        <CreateModel handleTableLoad={() => setLoading(true)} />
      </Group>

      {folderId && (
        <Group mt={8}>
          <Group w={400}>
            <FoldersDropdown
              type="Model"
              getClickLink={(id) => `/models/folder/${id}/model/none/type`}
              getDeletedLink={() => `/models/folder`}
            />
          </Group>

          <ModelTypeFilter
            filterType={typeFilter}
            setFilterType={setTypeFilter}
          />
        </Group>
      )}
      <Divider my={16} />

      <PanelGroup
        direction="horizontal"
        style={{ flex: 1 }}
        autoSaveId={modelType}
      >
        <Panel
          defaultSize={30}
          minSize={10}
          style={{ display: "flex", flexDirection: "column" }}
          collapsible
        >
          <Flex
            flex={1}
            pos={"relative"}
            style={{
              display: minState === "minimized" ? "none" : "flex",
            }}
            miw={380}
          >
            <NoItemsModal
              rows={rows}
              loading={loading}
              text={
                folderId ? (
                  <Text size="xl" mb={32}>
                    {lang.new_models.no_model_in_folder}
                  </Text>
                ) : (
                  <Text size="xl" mb={32}>
                    {lang.new_models.no_model}{" "}
                    {getModelTypeProps(modelType, folderId)?.name}
                  </Text>
                )
              }
              button={<CreateModel />}
            />
            <MainTable
              apiRef={apiRef}
              columns={columns}
              rows={filteredRows}
              loading={loading}
              setLoading={setLoading}
              id={"model-table"}
              toolbarActions={toolbarActions}
              actionsCell={actionsCell}
              onRowClick={handleRowClick}
              hasFolder
              pageName={getModelTypeProps(modelType, folderId)?.pageName}
              pageOrderName={
                getModelTypeProps(modelType, folderId)?.pageName + "_order"
              }
              defaultHighlight={highlight}
              setDefaultHighlight={setHighlight}
              whiteButtons={!model_id}
              actionsCellWidth={100}
              disableGotoPage={model_id}
              disableVisibleColumns={model_id}
              defaultPerPage={15}
              custom
              // disablePerPage={model_id}
              // disableCheckboxes={!!model_id}
              getRowClassName={(params) =>
                getRowClasses({
                  params,
                  mainId: modelId,
                  integerId: model_id,
                  isHoverSource: true,
                })
              }
              withSorter={!!model_id}
              tableSx={{
                "& .MuiDataGrid-columnHeaders": {
                  border: "none !important",
                },
                "& .MuiDataGrid-cell": {
                  border: "none",
                },
                "& .MuiDataGrid-main": {
                  background: "var(--mantine-color-primary-2)",
                  border: "none !important",
                  borderRadius: 3,
                },
                "& .table-search": {
                  width: model_id ? "200px !important" : "300px !important",
                },
                "& .MuiDataGrid-footerContainer": {
                  border: "none !important",
                },
                "& .table-row": {
                  cursor: "pointer",
                  borderRadius: 3,
                  marginBottom: 1,
                  background: "#fff",
                  border: "1px solid var(--mantine-color-primary-1)",
                },
                "& .MuiDataGrid-pinnedColumnHeaders": {
                  background: "transparent",
                  borderRight: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--right": {
                  background: "var(--mantine-color-primary-2) !important",
                  border: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--left": {
                  background: "var(--mantine-color-primary-2) !important",
                  border: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--right .table-row": {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  background: "#fff",
                  border: "1px solid   var(--mantine-color-primary-1)",
                  borderLeft: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--left .table-row": {
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  background: "#fff",
                  border: "1px solid   var(--mantine-color-primary-1)",
                  borderRight: "none !important",
                },
                "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
                  borderRadius: 0,
                },
                "& .MuiTablePagination-input": {
                  position: "relative",
                  zIndex: 1,
                },
              }}
              setHasQuery={setHasQuery}
            />
          </Flex>
        </Panel>

        {model_id && (
          <PanelResizeHandle
            style={{
              width: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "col-resize",
              backgroundColor: "var(--mantine-color-primary-3)",
              margin: "0 24px",
            }}
          >
            <Box
              style={{
                backgroundColor: "var(--mantine-color-gray-2)",
                borderRadius: "4px",
                display: "flex",
              }}
            >
              <ThemeIcon variant="filled" py={16}>
                <IconGripVertical size={24} />
              </ThemeIcon>
            </Box>
          </PanelResizeHandle>
        )}

        {model_id && (
          <Panel minSize={20} collapsible>
            <Stack
              flex={1}
              style={{ transition: "all ease .3s", overflow: "hidden" }}
              h={"100%"}
              pos={"relative"}
              miw={1000}
            >
              <CloseButton
                style={{
                  position: "absolute",
                  left: 8,
                  top: 8,
                  zIndex: 4,
                }}
                size={40}
                onClick={handleClose}
              />
              {model_id && !loading && (
                <ModelPageEdit
                  onEdited={() => {
                    // handleClose();
                    setLoading(true);
                  }}
                />
              )}
            </Stack>
          </Panel>
        )}
      </PanelGroup>
    </Stack>
  );
};

export default ModelListing;
